import { useMemo } from 'react';
import { RouterProvider } from 'quartz-routing';
import { Routes } from './components/Routes';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { GET_TICKET_THREAD_QUERY_POLICY } from './data/useTicketThread';
import { usePortalData } from './data/usePortalData';
import I18n from 'I18n';
import { AccessDenied } from './components/AccessDenied';
import { CenteredSpinner } from './components/CenteredSpinner';
import { useEffect } from 'react';
import { getPortalBasename, PortalsAppLayout, usePortalsAuth, trackPortalInitialized, getLocaleInfo as getPortalsLocaleInfo, queryLoginVerify } from 'portals-platform-lib';
import { NavigationProvider } from './hooks/useNavigationContext';

// @bend-expect quartz-no-create-browser-history
import { createBrowserHistory } from 'quartz-routing/react-router';
import { Metrics } from './utils/metrics';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { chirpClient } from './utils/chirpClient';
import { getPortalDataRpc } from 'customer-portal-app/__generated__/chirp/com/hubspot/support/portal/rpc/PortalDataRpc';
import { initializePortal } from 'portals-platform-lib';
import { PortalPlatformDataContext } from './hooks/usePortalPlatformData';
import { usageTracker } from './utils/usageTracker';
import { getIsPreview } from './utils/isPreview';
import { ListingsSortAndFiltersProvider } from './hooks/useListingsSortAndFiltersContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const client = new DataFetchingClient({
  queryFieldPolicies: Object.assign({}, GET_TICKET_THREAD_QUERY_POLICY)
});

/**
 * Note: This currently duplicates the usePortalData fetch, but it's called in a different context, so we can't use the hook directly.
 * I propose we eventually refactor the endpoint to give us just a language endpoint.
 */
export const getLocaleInfo = async () => {
  var _loginVerifyResult$da, _loginVerifyResult$da2;
  const loginVerifyResult = await queryLoginVerify({
    client
  }).catch(e => {
    console.error('getLocaleInfo: failed to query loginVerify', e);
    return undefined;
  });
  const isUngated = (_loginVerifyResult$da = loginVerifyResult === null || loginVerifyResult === void 0 || (_loginVerifyResult$da2 = loginVerifyResult.data) === null || _loginVerifyResult$da2 === void 0 || (_loginVerifyResult$da2 = _loginVerifyResult$da2.result) === null || _loginVerifyResult$da2 === void 0 || (_loginVerifyResult$da2 = _loginVerifyResult$da2.featuresEnabled) === null || _loginVerifyResult$da2 === void 0 ? void 0 : _loginVerifyResult$da2.includes('PortalsPlatform::SupportPreferredLanguage')) !== null && _loginVerifyResult$da !== void 0 ? _loginVerifyResult$da : false;
  if (isUngated) {
    return getPortalsLocaleInfo({
      client
    });
  }
  try {
    const preview = getIsPreview();
    const {
      language
    } = await chirpClient.call(getPortalDataRpc, {
      request: preview.isPreview ? {
        previewJwt: preview.previewJwt
      } : {}
    });
    return {
      locale: language,
      langEnabled: true,
      timezone: undefined,
      userObjectTimezone: undefined
    };
  } catch (error) {
    return {
      locale: navigator.language,
      langEnabled: false,
      timezone: undefined,
      userObjectTimezone: undefined
    };
  }
};
export const prepareApp = async () => {
  let portalPlatformData = null;
  try {
    const {
      portalData
    } = await initializePortal({
      client
    });
    portalPlatformData = portalData;
  } catch (error) {
    console.error('Failed to fetch brand and theme data:', error);
  }
  return {
    props: {
      dataFetchingClient: client,
      portalPlatformData
    }
  };
};
export function AppComponent({
  dataFetchingClient,
  portalPlatformData
}) {
  return /*#__PURE__*/_jsx(DataFetchingClientProvider, {
    client: dataFetchingClient,
    children: /*#__PURE__*/_jsx(PortalPlatformDataContext.Provider, {
      value: portalPlatformData,
      children: /*#__PURE__*/_jsx(AppContent, {})
    })
  });
}
function AppContent() {
  const {
    isPreview
  } = getIsPreview();
  if (isPreview) {
    return /*#__PURE__*/_jsx(AppShell, {
      isAuthLoading: false,
      isAccessDenied: false,
      user: undefined
    });
  }
  return /*#__PURE__*/_jsx(AuthenticatedAppContent, {});
}
function AuthenticatedAppContent() {
  const {
    isLoading,
    isAccessDenied,
    user
  } = usePortalsAuth({
    redirectUrl: window.location.href
  });
  return /*#__PURE__*/_jsx(AppShell, {
    isAuthLoading: isLoading,
    isAccessDenied: isAccessDenied,
    user: user
  });
}
function AppShell({
  isAuthLoading,
  isAccessDenied,
  user
}) {
  var _portalData$data2;
  const portalData = usePortalData();
  const isPreviewMode = getIsPreview().isPreview;
  useEffect(() => {
    usageTracker.setProperties({
      isEditing: isPreviewMode
    });
  }, [isPreviewMode]);
  useEffect(() => {
    var _portalData$data;
    if ((_portalData$data = portalData.data) !== null && _portalData$data !== void 0 && (_portalData$data = _portalData$data.settings) !== null && _portalData$data !== void 0 && _portalData$data.pageTitle) {
      document.title = portalData.data.settings.pageTitle;
    }
  }, [portalData.data]);
  const email = user === null || user === void 0 ? void 0 : user.email;
  useEffect(() => {
    if (email) {
      trackPortalInitialized({
        email
      });
    }
  }, [email]);
  const isLoading = portalData.loading || isAuthLoading;
  const history = useMemo(() => createBrowserHistory({
    basename: document.location.hostname.includes('hubspotqa.com') ? 'customer-portal-app' : getPortalBasename()
  }), []);
  useEffect(() => {
    if (!isLoading) {
      Metrics.timer('startup').update(performance.now());
    }
  }, [isLoading]);
  if (isAccessDenied) {
    return /*#__PURE__*/_jsx(AccessDenied, {});
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(CenteredSpinner, {
      ariaLabel: I18n.text('customer-portal-app.loading')
    });
  }

  /**
   * This prevents the app from rendering if the portal data is blank,
   * this can occur if the portal data endpoint has loaded but a 403 was returned,
   * prior to the redirect taking effect.
   */
  if (!((_portalData$data2 = portalData.data) !== null && _portalData$data2 !== void 0 && _portalData$data2.settings)) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(RouterProvider, {
      history: history,
      children: /*#__PURE__*/_jsx(NavigationProvider, {
        children: /*#__PURE__*/_jsx(ListingsSortAndFiltersProvider, {
          children: /*#__PURE__*/_jsx(PortalsAppLayout, {
            history: history,
            children: /*#__PURE__*/_jsx(Routes, {})
          })
        })
      })
    })]
  });
}